import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchBatches,
  fetchBatchesStart,
  fetchBatchesSuccess,
  fetchBatchesFailure,
  addBatch,
  addBatchStart,
  addBatchSuccess,
  addBatchFailure,
  editBatch,
  editBatchStart,
  editBatchSuccess,
  editBatchFailure,
  deleteBatch,
  deleteBatchStart,
  deleteBatchSuccess,
  deleteBatchFailure,
  generateProdSeal,
  generateProdSealStart,
  generateProdSealSuccess,
  generateProdSealFailure,
  publishBatch,
  publishBatchStart,
  publishBatchSuccess,
  publishBatchFailure,
  fetchBatchById,
  fetchBatchStart,
  fetchBatchSuccess,
  fetchBatchFailure,
} from "../slices/batchesSlice";
import { makeRequest } from "../../utils/api";
import { extractErrorMessage, extractErrorDetails } from "../../utils/helpers";
import { toast } from "react-toastify";
import { Batch } from "../../types/pages/batches";

function* fetchBatchesSaga(
  action: PayloadAction<{
    page: number;
    rowsPerPage: number;
    sortBy?: string;
    sortOrder?: number;
    productId?: string;
  }>,
): Generator<any, void, any> {
  try {
    yield put(fetchBatchesStart());
    const skip = action.payload.page * action.payload.rowsPerPage;
    const limit = action.payload.rowsPerPage;
    const sortBy = action.payload.sortBy || "created_at";
    const sortOrder =
      action.payload.sortOrder !== undefined ? action.payload.sortOrder : -1;
    const productFilter = action.payload.productId
      ? `&product_id=${action.payload.productId}`
      : "";

    const response = yield call(
      makeRequest,
      "get",
      `/batches/?skip=${skip}&limit=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}${productFilter}`,
    );
    yield put(fetchBatchesSuccess(response));
  } catch (error: any) {
    yield put(fetchBatchesFailure(extractErrorMessage(error)));
  }
}

function* addBatchSaga(
  action: PayloadAction<Omit<Batch, "_id" | "created_at" | "updated_at">>,
): Generator<any, void, any> {
  try {
    yield put(addBatchStart());
    const response: Batch = yield call(
      makeRequest,
      "post",
      "/batches/",
      action.payload,
    );
    yield put(addBatchSuccess(response));
    toast.success(response.message ?? "Batch added successfully!");
  } catch (error: any) {
    yield put(addBatchFailure(extractErrorMessage(error)));
  }
}

function* editBatchSaga(
  action: PayloadAction<{
    batchId: string;
    batchData: Partial<Omit<Batch, "_id" | "created_at" | "updated_at">>;
  }>,
): Generator<any, void, any> {
  try {
    yield put(editBatchStart());
    const response: Batch = yield call(
      makeRequest,
      "put",
      `/batches/${action.payload.batchId}`,
      action.payload.batchData,
    );
    yield put(editBatchSuccess(response));
    toast.success(response.message ?? "Batch updated successfully!");
  } catch (error: any) {
    yield put(editBatchFailure(extractErrorMessage(error)));
  }
}

function* deleteBatchSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(deleteBatchStart());
    yield call(makeRequest, "delete", `/batches/${action.payload}`);
    yield put(deleteBatchSuccess(action.payload));
    toast.success("Batch deleted successfully!");
  } catch (error: any) {
    yield put(deleteBatchFailure(extractErrorMessage(error)));
  }
}

function* fetchBatchByIdSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(fetchBatchStart());
    const response: Batch = yield call(
      makeRequest,
      "get",
      `/batches/${action.payload}`,
    );
    yield put(fetchBatchSuccess(response));
  } catch (error: any) {
    yield put(fetchBatchFailure(extractErrorMessage(error)));
  }
}

function* generateProdSealSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(generateProdSealStart());
    toast.success("ProdSeal generation started  !");
    yield call(makeRequest, "post", "/prodseals/", {
      batch_id: action.payload,
    });
    yield put(generateProdSealSuccess());
  } catch (error: any) {
    const errorDetails = extractErrorDetails(error);
    const errorMessage = extractErrorMessage(error);

    if (
      errorDetails?.message ===
      "All prodseals for this batch have already been generated"
    ) {
      toast.error("All prodseals for this batch have already been generated!");
    } else {
      toast.error(errorMessage);
    }

    yield put(generateProdSealFailure(errorMessage));
  }
}

function* publishBatchSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(publishBatchStart());
    const response: Batch = yield call(
      makeRequest,
      "get",
      `/batches/${action.payload}/publish`,
    );
    yield put(publishBatchSuccess(response));
    toast.success(response.message ?? "Batch published successfully");
  } catch (error: any) {
    yield put(publishBatchFailure(extractErrorMessage(error)));
  }
}

export default function* batchesSaga(): Generator<any, void, any> {
  yield takeLatest(fetchBatches.type, fetchBatchesSaga);
  yield takeLatest(addBatch.type, addBatchSaga);
  yield takeLatest(editBatch.type, editBatchSaga);
  yield takeLatest(deleteBatch.type, deleteBatchSaga);
  yield takeLatest(generateProdSeal.type, generateProdSealSaga);
  yield takeLatest(publishBatch.type, publishBatchSaga);
  yield takeLatest(fetchBatchById.type, fetchBatchByIdSaga);
}
